import React, { useEffect, useState, useCallback } from "react";
import { styled } from "@mui/system";
import Box from "@mui/material/Box";
import shopUploadIcon from "../../assets/shop-upload-icon.svg";
import { useDispatch } from "react-redux";
import { showToast } from "../../store/slices/common";
import { UploadRounded } from "@mui/icons-material";
import { validateFileExtension } from "../../utils";
import { CircularProgress } from "@mui/material";

const FlexContainer = styled(Box)({
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
});

const Circle = styled(Box)(() => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  position: "relative",
  backgroundColor: "transparent",
  borderRadius: "12px",
  border: `1px solid #0832DE`,
}));

const CurvedCorner = styled(Box)(() => ({
  position: "absolute",
  bottom: "15px",
  borderRadius: "10px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  background: "#FFFFFF",
  zIndex: 1,
  cursor: "pointer",
}));

const UploadImage = ({ image, onChange, style, id, disabled, name }) => {
  const dispatch = useDispatch();

  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);

  const processFile = useCallback((file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onloadend = () => {
      if (validateFileExtension(file?.name, name)) {
        setPreview(reader.result);
        setLoading(true);
        onChange(file);
        setLoading(false);
      } else {
        dispatch(
          showToast({ color: "error", msg: "Please Upload Valid imgs" })
        );
      }
    };
    reader.readAsDataURL(file);
  }, [name, onChange, dispatch]);

  const handleFileUpload = (event) => {
    const file = event?.target?.files && event?.target?.files[0];
    processFile(file);
  };

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled) setIsDragging(true);
  }, [disabled]);

  const handleDragEnter = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled) setIsDragging(true);
  }, [disabled]);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    if (disabled) return;

    const files = e.dataTransfer.files;
    if (!files || files.length === 0) return;

    const file = files[0];
    if (!file.type.startsWith("image/")) {
      dispatch(showToast({ color: "error", msg: "Please drop an image file" }));
      return;
    }

    processFile(file);
  }, [disabled, processFile, dispatch]);

  useEffect(() => {
    if (image && typeof image === "string") {
      setPreview(image);
    }
  }, [image]);

  return (
    <>
      <FlexContainer
        sx={{ justifyContent: "flex-start" }}
        margin="8px 0 20px 0"
      >
        <Circle
          className={"uploadimgcircle"}
          onDragOver={handleDragOver}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          sx={{
            width: style?.width || "200px",
            height: style?.height || "200px",
            borderColor: isDragging ? "#0832DE" : "#0832DE",
            borderWidth: isDragging ? "2px" : "1px",
            borderStyle: isDragging ? "dashed" : "solid",
            background: isDragging ? "rgba(8, 50, 222, 0.06)" : "transparent",
            transition: "all 0.2s ease",
            cursor: disabled ? "default" : "pointer",
          }}
        >
          {preview ? (
            <img
              src={preview}
              alt=""
              style={{
                width: "100%",
                height: "100%",
                margin: "0.1rem",
                borderRadius: "12px",
                objectFit: "fill",
                opacity: isDragging ? 0.5 : 1,
                transition: "opacity 0.2s ease",
              }}
            />
          ) : (
            <>
              {loading ? (
                <CircularProgress size={40} />
              ) : isDragging ? (
                <span
                  style={{
                    color: "#0832DE",
                    fontWeight: 600,
                    fontSize: "13px",
                    textAlign: "center",
                  }}
                >
                  Drop image here
                </span>
              ) : (
                <img
                  alt=""
                  src={shopUploadIcon}
                  style={{
                    width: "50px",
                    height: "50px",
                    objectFit: "fill",
                  }}
                />
              )}
            </>
          )}
          {isDragging && preview && (
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                borderRadius: "12px",
                background: "rgba(8, 50, 222, 0.1)",
                pointerEvents: "none",
              }}
            >
              <span style={{ color: "#0832DE", fontWeight: 600, fontSize: "13px" }}>
                Drop to replace
              </span>
            </Box>
          )}
          <CurvedCorner
            sx={{
              right: style?.iconRight || "-25px",
              width: style?.iconWidth || "50px",
              height: style?.iconHeight || "50px",
              border: disabled ? "2px solid #6a6a6a" : "2px solid #0832DE",
            }}
          >
            <input
              type="file"
              id={id}
              style={{ display: "none" }}
              onChange={handleFileUpload}
              accept="image/*"
              disabled={disabled ? true : false}
            />
            <label
              htmlFor={id}
              style={{ display: "flex", alignItems: "center" }}
            >
              <UploadRounded
                style={{
                  cursor: "pointer",
                  color: disabled ? "#6a6a6a" : "#0832DE",
                }}
              />
            </label>
          </CurvedCorner>
        </Circle>
      </FlexContainer>
    </>
  );
};

export default UploadImage;
